import {debounce} from 'lodash';
import uuid from 'uuid-random';

import {createObjectPropertyUpdater, createUpdater} from '../../../../srcCommon/helpers/updateHelpers';
import {DeferredFunc} from '../../../helpers/asyncHelpers';
import {
  addCounterValuesToClientCounters,
  computeCountersForConversations,
  CounterValues,
  emptyCounterValues,
  negateCounterValues,
  sumCounterValues,
} from '../../../helpers/counterHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {BadgeCountersModel, BoldCountersModel, CountersModel} from '../../../models/api/countersModel';
import {Dispatch} from '../../appStore';
import {ThunkAction} from '../../appThunk';
import {
  finishOptimisticUpdateAction,
  startOptimisticUpdateAction,
} from '../../optimisticUpdates/optimisticUpdatesActions';
import {DataState} from '../dataState';

/*
 * Action types.
 */

export enum CountersActionsEnum {
  NOTIFICATIONS_COUNTER_UPDATED = '@@data/counters/NOTIFICATIONS_COUNTER_UPDATED',
  BADGE_COUNTERS_UPDATED = '@@data/counters/BADGE_COUNTERS_UPDATED',
  BADGE_UNREAD_COUNTERS_UPDATED = '@@data/counters/BADGE_UNREAD_COUNTERS_UPDATED',
  BOLD_COUNTERS_UPDATED = '@@data/counters/BOLD_COUNTERS_UPDATED',
  DELEGATION_COUNTERS_UPDATED = '@@data/counters/DELEGATION_COUNTERS_UPDATED',
}

/*
 * Types.
 */

export interface NotificationsCounterUpdatedAction extends ActionWithPayload<number> {
  type: CountersActionsEnum.NOTIFICATIONS_COUNTER_UPDATED;
}

interface BadgeCountersUpdatedPayload {
  badge: BadgeCountersModel;
  isPartial: boolean;
}

export interface BadgeCountersUpdatedAction extends ActionWithPayload<BadgeCountersUpdatedPayload> {
  type: CountersActionsEnum.BADGE_COUNTERS_UPDATED;
}

interface BadgeUnreadCountersUpdatedPayload {
  badgeUnread: BadgeCountersModel;
  isPartial: boolean;
}

export interface BadgeUnreadCountersUpdatedAction
  extends ActionWithPayload<BadgeUnreadCountersUpdatedPayload> {
  type: CountersActionsEnum.BADGE_UNREAD_COUNTERS_UPDATED;
}

interface BoldCountersUpdatedPayload {
  bold: BoldCountersModel;
  isPartial: boolean;
}

export interface BoldCountersUpdatedAction extends ActionWithPayload<BoldCountersUpdatedPayload> {
  type: CountersActionsEnum.BOLD_COUNTERS_UPDATED;
}

interface DelegationCountersUpdatedPayload {
  delegationId: number;
  counters: BadgeCountersModel;
  isPartial: boolean;
}

export interface DelegationCountersUpdatedAction extends ActionWithPayload<DelegationCountersUpdatedPayload> {
  type: CountersActionsEnum.DELEGATION_COUNTERS_UPDATED;
}

/*
 * Action creators.
 */

export function updateNotificationsCounter(
  newNotificationsCounter: number,
): NotificationsCounterUpdatedAction {
  return {
    type: CountersActionsEnum.NOTIFICATIONS_COUNTER_UPDATED,
    payload: newNotificationsCounter,
  };
}

export function updateBadgeCounters(badge: BadgeCountersModel): BadgeCountersUpdatedAction {
  return {
    type: CountersActionsEnum.BADGE_COUNTERS_UPDATED,
    payload: {badge, isPartial: false},
  };
}

export function updateBadgeUnreadCounters(badgeUnread: BadgeCountersModel): BadgeUnreadCountersUpdatedAction {
  return {
    type: CountersActionsEnum.BADGE_UNREAD_COUNTERS_UPDATED,
    payload: {badgeUnread, isPartial: false},
  };
}

export function updateBoldCounters(bold: BoldCountersModel): BoldCountersUpdatedAction {
  return {
    type: CountersActionsEnum.BOLD_COUNTERS_UPDATED,
    payload: {bold, isPartial: false},
  };
}

export function updateDelegationCounters(
  delegationId: number,
  counters: BadgeCountersModel,
): DelegationCountersUpdatedAction {
  return {
    type: CountersActionsEnum.DELEGATION_COUNTERS_UPDATED,
    payload: {delegationId, counters, isPartial: false},
  };
}

/*
 * Partial updates.
 */

export function partialUpdateBadgeCounters(badge: BadgeCountersModel): BadgeCountersUpdatedAction {
  return {
    type: CountersActionsEnum.BADGE_COUNTERS_UPDATED,
    payload: {badge, isPartial: true},
  };
}

export function partialUpdateBadgeUnreadCounters(
  badgeUnread: BadgeCountersModel,
): BadgeUnreadCountersUpdatedAction {
  return {
    type: CountersActionsEnum.BADGE_UNREAD_COUNTERS_UPDATED,
    payload: {badgeUnread, isPartial: true},
  };
}

export function partialUpdateBoldCounters(bold: BoldCountersModel): BoldCountersUpdatedAction {
  return {
    type: CountersActionsEnum.BOLD_COUNTERS_UPDATED,
    payload: {bold, isPartial: true},
  };
}

export function partialUpdateDelegationCounters(
  delegationId: number,
  counters: BadgeCountersModel,
): DelegationCountersUpdatedAction {
  return {
    type: CountersActionsEnum.DELEGATION_COUNTERS_UPDATED,
    payload: {delegationId, counters, isPartial: true},
  };
}

/*
 * Client-side counters.
 */

type ClientCountersDebouncer = DeferredFunc<(callback: () => void) => void>;

export const clientCountersOptimisticUpdateId = uuid();
const clientCountersTimeout = 5000;
const clientCountersDebouncerMap = new WeakMap<Dispatch, ClientCountersDebouncer>();

/** Returns a shared debouncer for the given dispatcher, to avoid global state and dependent tests. */
function findClientCountersDebouncer(dispatch: Dispatch): ClientCountersDebouncer {
  const existingDebouncer = clientCountersDebouncerMap.get(dispatch);
  if (existingDebouncer) {
    return existingDebouncer;
  }

  const newDebouncer = debounce((callback: () => void) => callback(), clientCountersTimeout);
  clientCountersDebouncerMap.set(dispatch, newDebouncer);
  return newDebouncer;
}

let clientCounterValues: CounterValues = emptyCounterValues;

/**
 * Applies the given counters deltas and ignores server counter updates with a 5 second debounce to allow
 * client and server counters to converge.
 */
export function updateClientCountersAction(counterDeltas: CounterValues): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();

    // Update the client-side counters.
    const newClientCounterValues = addCounterValuesToClientCounters(
      state.originalData.counters,
      clientCounterValues,
      counterDeltas,
    );
    clientCounterValues = newClientCounterValues;

    // Create a memoized updater for the counters state.
    const countersUpdater = createUpdater((counters: CountersModel) => ({
      ...counters,
      badge: {
        ...counters.badge,
        values: {
          ...counters.badge.values,
          ...newClientCounterValues.badge,
        },
      },
      badgeUnread: {
        ...counters.badgeUnread,
        values: {
          ...counters.badgeUnread.values,
          ...newClientCounterValues.badgeUnread,
        },
      },
    }));

    // Create a memoized updater for the data state.
    const dataUpdater = createObjectPropertyUpdater<DataState, 'counters'>('counters', countersUpdater);

    // Start (or restart) the optimistic update for the client-side counters.
    dispatch(startOptimisticUpdateAction(clientCountersOptimisticUpdateId, dataUpdater));

    // Clear the optimistic update for the client-side counters with a debounce.
    const withDebounce = findClientCountersDebouncer(dispatch);
    withDebounce(() => {
      dispatch(clearClientCountersAction());
    });
  };
}

export function updateClientCountersForConversationsAction(
  conversationIds: ReadonlyArray<number>,
  updateConversations: () => void,
): ThunkAction {
  return (dispatch, getState) => {
    if (conversationIds.length === 0) {
      updateConversations();
      return;
    }

    batch(() => {
      // Calculate the counters for the conversations before we update them.
      const beforeState = getState();

      const beforeCounterValues = computeCountersForConversations(beforeState.originalData, conversationIds);

      updateConversations();

      // Calculate the counters for the conversations after we have updated them.
      const afterState = getState();
      const afterCounterValues = computeCountersForConversations(afterState.originalData, conversationIds);

      // Calculate the counter deltas.
      const beforeCounterDeltas = negateCounterValues(beforeCounterValues);
      const afterCounterDeltas = afterCounterValues;
      const counterDeltas = sumCounterValues(beforeCounterDeltas, afterCounterDeltas);

      // Update the client-side counters.
      dispatch(updateClientCountersAction(counterDeltas));
    });
  };
}

export function clearClientCountersAction(): ThunkAction {
  return (dispatch) => {
    dispatch(finishOptimisticUpdateAction(clientCountersOptimisticUpdateId));
    clientCounterValues = emptyCounterValues;

    findClientCountersDebouncer(dispatch).cancel();
  };
}

/*
 * Union type.
 */

export type CountersAction =
  | NotificationsCounterUpdatedAction
  | BadgeCountersUpdatedAction
  | BadgeUnreadCountersUpdatedAction
  | BoldCountersUpdatedAction
  | DelegationCountersUpdatedAction;
